import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { widgetRegistry } from '../components/widgets';

const DEFAULT_WIDGETS = ['stats', 'recent-entries', 'revenue', 'backup', 'shelly', 'team', 'analytics'];

// All available tabs in the app
const ALL_TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'customers', label: 'Customers' },
  { id: 'entries', label: 'Time Entries' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'reports', label: 'Reports' },
  { id: 'settings', label: 'Settings' },
  { id: 'users', label: 'Users' },
  { id: 'accounting', label: 'Accounting' },
  { id: 'monitoring', label: 'Monitoring' },
  { id: 'social', label: 'Social Media' },
  { id: 'atera', label: 'Tickets' },
  { id: 'chats', label: 'Messages' },
  { id: 'bugs', label: 'Bugs' }
];

const ROLES = [
  { id: 'technician', label: 'Technician' },
  { id: 'employee', label: 'Employee' },
  { id: 'admin', label: 'Admin' },
  { id: 'superadmin', label: 'Superadmin' }
];

const Settings = ({ darkMode: propDarkMode }) => {
  const { user: currentUser } = useAuth();
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('ngx-mc-dark-mode');
    return saved ? JSON.parse(saved) : false;
  });
  
  // Mobile detection
  const [windowWidth, setWindowWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const isMobile = windowWidth < 800;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isSmallTablet = windowWidth >= 768 && windowWidth < 1024;
  
  const [enabledWidgets, setEnabledWidgets] = useState(() => {
    const saved = localStorage.getItem('ngx-mc-widgets');
    return saved ? JSON.parse(saved) : DEFAULT_WIDGETS;
  });
  const [saved, setSaved] = useState(false);
  
  // Sync with prop darkMode
  useEffect(() => {
    if (propDarkMode !== undefined) {
      setDarkMode(propDarkMode);
    }
  }, [propDarkMode]);
  
  // Save dark mode preference
  useEffect(() => {
    localStorage.setItem('ngx-mc-dark-mode', JSON.stringify(darkMode));
  }, [darkMode]);
  
  // User management state
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedUserWidgets, setSelectedUserWidgets] = useState([]);
  const [selectedUserTabs, setSelectedUserTabs] = useState(null);
  const [userSaved, setUserSaved] = useState(false);
  
  // Selector: 'users' or 'groups'
  const [selectionType, setSelectionType] = useState('users');
  
  // New group form
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  
  // New user form
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUserData, setNewUserData] = useState({
    username: '', password: '', name: '', email: '', phone: '', role: 'employee', hourlyRate: 135
  });
  
  // Selected user role for editing
  const [selectedUserRole, setSelectedUserRole] = useState('employee');
  
  // Active settings tab
  const [activeTab, setActiveTab] = useState('personal');
  
  // My Profile state
  const [profileData, setProfileData] = useState({ name: '', email: '', phone: '' });
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  const isSuperadmin = currentUser?.role === 'superadmin';
  const canManage = isSuperadmin; // Only superadmins can manage users/groups

  const textColor = darkMode ? '#f3f4f6' : '#111827';
  const subTextColor = darkMode ? '#9ca3af' : '#6b7280';
  const bgSecondary = darkMode ? '#374151' : '#f3f4f4';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';
  const accentColor = '#3b82f6';

  // Fetch users and groups
  useEffect(() => {
    if (canManage) {
      fetch('/api/users', { credentials: 'include' })
        .then(r => r.json())
        .then(data => setUsers(data || []))
        .catch(console.error);
        
      fetch('/api/users/groups', { credentials: 'include' })
        .then(r => r.json())
        .then(data => setGroups(data || []))
        .catch(console.error);
    }
  }, [canManage]);

  // Fetch current user's profile
  useEffect(() => {
    fetch('/api/users/me', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data) {
          setProfileData({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || ''
          });
        }
      })
      .catch(console.error);
  }, []);

  // Fetch selected user's widgets and tabs
  useEffect(() => {
    if (canManage && selectedUserId) {
      // Get current user data to set their role
      const user = users.find(u => u.id === parseInt(selectedUserId));
      if (user) {
        setSelectedUserRole(user.role || 'employee');
      }
      
      fetch(`/api/users/${selectedUserId}/widgets`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => setSelectedUserWidgets(data.widgets || []))
        .catch(console.error);
      
      fetch(`/api/users/${selectedUserId}/tabs`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => setSelectedUserTabs(data.tabs))
        .catch(console.error);
    }
    
    if (canManage && selectedGroupId) {
      const group = groups.find(g => g.id === parseInt(selectedGroupId));
      if (group) {
        setSelectedUserWidgets(group.widgets ? JSON.parse(group.widgets) : []);
        setSelectedUserTabs(group.tabs ? JSON.parse(group.tabs) : null);
      }
    }
  }, [canManage, selectedUserId, selectedGroupId, groups]);

  const toggleWidget = (widgetId) => {
    if ((selectedUserId || selectedGroupId) && canManage) {
      setSelectedUserWidgets(prev => {
        if (prev.includes(widgetId)) {
          return prev.filter(w => w !== widgetId);
        }
        return [...prev, widgetId];
      });
      setUserSaved(false);
    } else {
      setEnabledWidgets(prev => {
        if (prev.includes(widgetId)) {
          return prev.filter(w => w !== widgetId);
        }
        return [...prev, widgetId];
      });
      setSaved(false);
    }
  };

  const handleSavePersonal = () => {
    localStorage.setItem('ngx-mc-widgets', JSON.stringify(enabledWidgets));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleResetPersonal = () => {
    setEnabledWidgets(DEFAULT_WIDGETS);
    localStorage.setItem('ngx-mc-widgets', JSON.stringify(DEFAULT_WIDGETS));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  // Save user's or group's widget and tab permissions (superadmin)
  const handleSaveUserSettings = async () => {
    try {
      const targetId = selectionType === 'users' ? selectedUserId : selectedGroupId;
      const endpoint = selectionType === 'users' 
        ? `/api/users/${targetId}/widgets`
        : `/api/users/groups/${targetId}/widgets`;
      
      // Save widgets
      await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ widgets: selectedUserWidgets })
      });
      
      // Save tabs
      await fetch(endpoint.replace('/widgets', '/tabs'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tabs: selectedUserTabs })
      });
      
      // Save user role if editing a user
      if (selectionType === 'users' && selectedUserId) {
        await fetch(`/api/users/${selectedUserId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ role: selectedUserRole })
        });
        // Refresh users list
        const res = await fetch('/api/users', { credentials: 'include' });
        const updatedUsers = await res.json();
        setUsers(updatedUsers);
      }
      
      setUserSaved(true);
      setTimeout(() => setUserSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save settings:', err);
    }
  };
  
  // Toggle a tab
  const toggleTab = (tabId) => {
    setSelectedUserTabs(prev => {
      if (prev === null) {
        return [tabId];
      }
      if (prev.includes(tabId)) {
        const filtered = prev.filter(t => t !== tabId);
        return filtered.length === 0 ? null : filtered;
      }
      return [...prev, tabId];
    });
    setUserSaved(false);
  };
  
  const handleResetTabs = () => {
    setSelectedUserTabs(null);
    setUserSaved(false);
  };
  
  // Create new group
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    
    try {
      const res = await fetch('/api/users/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newGroupName, description: newGroupDesc })
      });
      
      if (res.ok) {
        const newGroup = await res.json();
        setGroups([...groups, newGroup]);
        setNewGroupName('');
        setNewGroupDesc('');
        setShowNewGroup(false);
      }
    } catch (err) {
      console.error('Failed to create group:', err);
    }
  };

  // Create new user
  const handleCreateUser = async () => {
    if (!newUserData.username.trim() || !newUserData.password.trim()) return;
    
    try {
      const res = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newUserData)
      });
      
      if (res.ok) {
        const newUser = await res.json();
        setUsers([...users, newUser]);
        setNewUserData({ username: '', password: '', name: '', email: '', phone: '', role: 'employee', hourlyRate: 135 });
        setShowNewUser(false);
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to create user');
      }
    } catch (err) {
      console.error('Failed to create user:', err);
    }
  };
  
  // Delete user
  const handleDeleteUser = async () => {
    if (!selectedUserId) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const res = await fetch(`/api/users/${selectedUserId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        setUsers(users.filter(u => u.id !== selectedUserId));
        setSelectedUserId(null);
      }
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  };

  const activeWidgets = (selectedUserId || selectedGroupId) ? selectedUserWidgets : enabledWidgets;
  const handleSave = (selectedUserId || selectedGroupId) ? handleSaveUserSettings : handleSavePersonal;
  const isEditing = selectedUserId || selectedGroupId;

  // Filter widgets by category
  const businessWidgets = Object.keys(widgetRegistry).filter(w => !['analytics', 'backup', 'shelly'].includes(w));
  const infraWidgets = Object.keys(widgetRegistry).filter(w => ['analytics', 'backup', 'shelly'].includes(w));

  // Tab button style
  const getTabStyle = (tabId) => ({
    padding: isMobile ? '12px 16px' : '10px 20px',
    borderRadius: '4px 4px 0 0',
    border: 'none',
    background: activeTab === tabId ? (darkMode ? '#1f2937' : '#fff') : 'transparent',
    color: activeTab === tabId ? accentColor : subTextColor,
    cursor: 'pointer',
    fontWeight: activeTab === tabId ? 'bold' : 'normal',
    fontSize: isMobile ? 14 : 15,
    borderBottom: activeTab === tabId ? `2px solid ${accentColor}` : 'none',
    marginRight: 4,
    display: 'inline-block'
  });

  // Section card style
  const cardStyle = {
    background: darkMode ? '#1f2937' : '#fff',
    borderRadius: 8,
    padding: 20,
    marginBottom: 20
  };

  // ==================== PERSONAL TAB ====================
  const renderPersonalTab = () => (
    <>
      {/* Personal Widgets */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ color: textColor, marginTop: 0 }}>Your Widgets</h3>
          <button 
            onClick={handleResetPersonal}
            style={{ padding: '6px 12px', background: '#6b7280', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
          >
            Reset to Default
          </button>
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <span style={{ color: subTextColor, fontSize: 14 }}>Business:</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {businessWidgets.map(w => (
              <button
                key={w}
                onClick={() => toggleWidget(w)}
                style={{
                  padding: isMobile ? '4px 8px' : '6px 12px',
                  borderRadius: 4,
                  border: `1px solid ${enabledWidgets.includes(w) ? '#22c55e' : borderColor}`,
                  background: enabledWidgets.includes(w) ? '#22c55e' : 'transparent',
                  color: enabledWidgets.includes(w) ? 'white' : textColor,
                  cursor: 'pointer',
                  fontSize: isMobile ? 11 : 13,
                  minWidth: isMobile ? '60px' : 'auto',
                  flex: isMobile ? '0 0 auto' : 'auto'
                }}
              >
                {w}
              </button>
            ))}
          </div>
        </div>
        
        <div>
          <span style={{ color: subTextColor, fontSize: 14 }}>Infrastructure:</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {infraWidgets.map(w => (
              <button
                key={w}
                onClick={() => toggleWidget(w)}
                style={{
                  padding: isMobile ? '4px 8px' : '6px 12px',
                  borderRadius: 4,
                  border: `1px solid ${enabledWidgets.includes(w) ? '#22c55e' : borderColor}`,
                  background: enabledWidgets.includes(w) ? '#22c55e' : 'transparent',
                  color: enabledWidgets.includes(w) ? 'white' : textColor,
                  cursor: 'pointer',
                  fontSize: isMobile ? 11 : 13,
                  minWidth: isMobile ? '60px' : 'auto',
                  flex: isMobile ? '0 0 auto' : 'auto'
                }}
              >
                {w}
              </button>
            ))}
          </div>
        </div>
        
        <button 
          onClick={handleSavePersonal}
          style={{ 
            marginTop: 16, 
            padding: '10px 20px', 
            background: accentColor, 
            color: 'white', 
            border: 'none', 
            borderRadius: 4, 
            cursor: 'pointer',
            fontSize: 14 
          }}
        >
          {saved ? '✓ Saved!' : 'Save Widgets'}
        </button>
      </div>
    </>
  );

  // ==================== MY PROFILE TAB ====================
  const handleSaveProfile = async () => {
    setProfileLoading(true);
    try {
      const res = await fetch('/api/users/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(profileData)
      });
      if (res.ok) {
        setProfileSaved(true);
        setTimeout(() => setProfileSaved(false), 2000);
      }
    } catch (err) {
      console.error('Failed to save profile:', err);
    }
    setProfileLoading(false);
  };

  const renderProfileTab = () => (
    <>
      <div style={cardStyle}>
        <h3 style={{ color: textColor, marginTop: 0, marginBottom: 20 }}>My Profile</h3>
        
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', color: textColor, marginBottom: 6, fontWeight: 500 }}>Name</label>
          <input
            type="text"
            value={profileData.name}
            onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 4,
              border: `1px solid ${borderColor}`,
              background: darkMode ? '#1f2937' : 'white',
              color: textColor,
              fontSize: 14
            }}
          />
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', color: textColor, marginBottom: 6, fontWeight: 500 }}>Email</label>
          <input
            type="email"
            value={profileData.email}
            onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 4,
              border: `1px solid ${borderColor}`,
              background: darkMode ? '#1f2937' : 'white',
              color: textColor,
              fontSize: 14
            }}
          />
        </div>
        
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', color: textColor, marginBottom: 6, fontWeight: 500 }}>Phone</label>
          <input
            type="tel"
            value={profileData.phone}
            onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 4,
              border: `1px solid ${borderColor}`,
              background: darkMode ? '#1f2937' : 'white',
              color: textColor,
              fontSize: 14
            }}
          />
        </div>
        
        <button 
          onClick={handleSaveProfile}
          disabled={profileLoading}
          style={{ 
            padding: '10px 24px', 
            background: profileSaved ? '#22c55e' : accentColor, 
            color: 'white', 
            border: 'none', 
            borderRadius: 4, 
            cursor: profileLoading ? 'not-allowed' : 'pointer',
            fontSize: 14,
            opacity: profileLoading ? 0.7 : 1
          }}
        >
          {profileSaved ? '✓ Saved!' : profileLoading ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
    </>
  );

  // ==================== USERS TAB ====================
  const renderUsersTab = () => (
    <>
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ color: textColor, marginTop: 0 }}>Users</h3>
          <button 
            onClick={() => setShowNewUser(!showNewUser)}
            style={{ padding: '8px 16px', background: accentColor, color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >
            + New User
          </button>
        </div>
        
        {/* New User Form */}
        {showNewUser && (
          <div style={{ background: bgSecondary, padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <h4 style={{ color: textColor, marginTop: 0, marginBottom: 12 }}>Create New User</h4>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr 1fr' : '1fr 1fr'), gap: 12 }}>
              <input
                type="text"
                placeholder="Username"
                value={newUserData.username}
                onChange={(e) => setNewUserData({...newUserData, username: e.target.value})}
                style={{ padding: 8, borderRadius: 4, border: `1px solid ${borderColor}`, background: darkMode ? '#374151' : '#fff', color: textColor }}
              />
              <input
                type="password"
                placeholder="Password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                style={{ padding: 8, borderRadius: 4, border: `1px solid ${borderColor}`, background: darkMode ? '#374151' : '#fff', color: textColor }}
              />
              <input
                type="text"
                placeholder="Full Name"
                value={newUserData.name}
                onChange={(e) => setNewUserData({...newUserData, name: e.target.value})}
                style={{ padding: 8, borderRadius: 4, border: `1px solid ${borderColor}`, background: darkMode ? '#374151' : '#fff', color: textColor }}
              />
              <input
                type="email"
                placeholder="Email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                style={{ padding: 8, borderRadius: 4, border: `1px solid ${borderColor}`, background: darkMode ? '#374151' : '#fff', color: textColor }}
              />
              <input
                type="text"
                placeholder="Phone"
                value={newUserData.phone}
                onChange={(e) => setNewUserData({...newUserData, phone: e.target.value})}
                style={{ padding: 8, borderRadius: 4, border: `1px solid ${borderColor}`, background: darkMode ? '#374151' : '#fff', color: textColor }}
              />
              <select
                value={newUserData.role}
                onChange={(e) => setNewUserData({...newUserData, role: e.target.value})}
                style={{ padding: 8, borderRadius: 4, border: `1px solid ${borderColor}`, background: darkMode ? '#374151' : '#fff', color: textColor }}
              >
                <option value="employee">Employee</option>
                <option value="admin">Admin</option>
                <option value="superadmin">Superadmin</option>
              </select>
            </div>
            <button 
              onClick={async () => {
                if (!newUserData.username || !newUserData.password) return;
                try {
                  const res = await fetch('/api/users', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(newUserData)
                  });
                  if (res.ok) {
                    const newUser = await res.json();
                    setUsers([...users, newUser]);
                    setShowNewUser(false);
                    setNewUserData({ username: '', password: '', name: '', email: '', phone: '', role: 'employee', hourlyRate: 135 });
                  }
                } catch (err) { console.error(err); }
              }}
              style={{ marginTop: 12, padding: '8px 16px', background: '#22c55e', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
            >
              Create User
            </button>
          </div>
        )}
        
        {/* User List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {users.map(user => (
            <div
              key={user.id}
              onClick={() => setSelectedUserId(user.id)}
              style={{
                padding: '12px 16px',
                borderRadius: 4,
                border: `2px solid ${selectedUserId === user.id ? accentColor : borderColor}`,
                background: selectedUserId === user.id ? (darkMode ? '#374151' : '#eff6ff') : 'transparent',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <div style={{ color: textColor, fontWeight: 'bold' }}>{user.name || user.username}</div>
                <div style={{ color: subTextColor, fontSize: 13 }}>{user.email} • {user.role}</div>
              </div>
              <span style={{ 
                padding: '4px 8px', 
                borderRadius: 4, 
                background: user.role === 'superadmin' ? '#8b5cf6' : user.role === 'admin' ? '#3b82f6' : '#6b7280',
                color: 'white',
                fontSize: 12
              }}>
                {user.role}
              </span>
            </div>
          ))}
        </div>
      </div>
      
      {/* User Permissions (when selected) */}
      {selectedUserId && (
        <>
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: textColor, marginTop: 0 }}>
                Edit: {users.find(u => u.id === selectedUserId)?.name || users.find(u => u.id === selectedUserId)?.username}
              </h3>
              <div>
                <button 
                  onClick={handleDeleteUser}
                  style={{ padding: '8px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                >
                  Delete
                </button>
                <button 
                  onClick={() => setSelectedUserId(null)}
                  style={{ marginLeft: 8, padding: '8px 12px', background: '#6b7280', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                >
                  Done
                </button>
              </div>
            </div>
            
            {/* Role Selector */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ color: textColor, marginBottom: 12 }}>Role</h4>
              <div style={{ display: 'flex', gap: 8 }}>
                {ROLES.map(r => (
                  <button
                    key={r.id}
                    onClick={() => { setSelectedUserRole(r.id); setUserSaved(false); }}
                    style={{
                      padding: '10px 20px',
                      borderRadius: 4,
                      border: `2px solid ${selectedUserRole === r.id ? accentColor : borderColor}`,
                      background: selectedUserRole === r.id ? accentColor : 'transparent',
                      color: selectedUserRole === r.id ? 'white' : textColor,
                      cursor: 'pointer'
                    }}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Widget Permissions */}
            <div>
              <h4 style={{ color: textColor, marginBottom: 12 }}>Widget Access</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {businessWidgets.concat(infraWidgets).map(w => (
                  <button
                    key={w}
                    onClick={() => toggleWidget(w)}
                    style={{
                      padding: isMobile ? '4px 8px' : '6px 12px',
                      borderRadius: 4,
                      border: `1px solid ${selectedUserWidgets.includes(w) ? '#22c55e' : borderColor}`,
                      background: selectedUserWidgets.includes(w) ? '#22c55e' : 'transparent',
                      color: selectedUserWidgets.includes(w) ? 'white' : textColor,
                      cursor: 'pointer',
                      fontSize: isMobile ? 11 : 13,
                      minWidth: isMobile ? '60px' : 'auto',
                      flex: isMobile ? '0 0 auto' : 'auto'
                    }}
                  >
                    {w}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Tab Permissions */}
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ color: textColor, margin: 0 }}>Tab Access</h4>
                <button 
                  onClick={handleResetTabs}
                  style={{ padding: '4px 8px', background: '#6b7280', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                >
                  Allow All
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {ALL_TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => toggleTab(tab.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 4,
                      border: `1px solid ${(selectedUserTabs === null || selectedUserTabs.includes(tab.id)) ? '#22c55e' : borderColor}`,
                      background: (selectedUserTabs === null || selectedUserTabs.includes(tab.id)) ? '#22c55e' : 'transparent',
                      color: (selectedUserTabs === null || selectedUserTabs.includes(tab.id)) ? 'white' : textColor,
                      cursor: 'pointer',
                      fontSize: 13
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            
            <button 
              onClick={handleSaveUserSettings}
              style={{ 
                marginTop: 20, 
                padding: '10px 20px', 
                background: accentColor, 
                color: 'white', 
                border: 'none', 
                borderRadius: 4, 
                cursor: 'pointer',
                fontSize: 14 
              }}
            >
              {userSaved ? '✓ Saved!' : 'Save User Settings'}
            </button>
          </div>
        </>
      )}
    </>
  );

  // ==================== GROUPS TAB ====================
  const renderGroupsTab = () => (
    <>
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ color: textColor, marginTop: 0 }}>Groups</h3>
          <button 
            onClick={() => setShowNewGroup(!showNewGroup)}
            style={{ padding: '8px 16px', background: '#22c55e', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >
            + New Group
          </button>
        </div>
        
        {/* New Group Form */}
        {showNewGroup && (
          <div style={{ background: bgSecondary, padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <h4 style={{ color: textColor, marginTop: 0, marginBottom: 12 }}>Create New Group</h4>
            <input
              type="text"
              placeholder="Group Name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              style={{ padding: 8, borderRadius: 4, border: `1px solid ${borderColor}`, background: darkMode ? '#374151' : '#fff', color: textColor, width: '100%', marginBottom: 8 }}
            />
            <input
              type="text"
              placeholder="Description (optional)"
              value={newGroupDesc}
              onChange={(e) => setNewGroupDesc(e.target.value)}
              style={{ padding: 8, borderRadius: 4, border: `1px solid ${borderColor}`, background: darkMode ? '#374151' : '#fff', color: textColor, width: '100%', marginBottom: 12 }}
            />
            <button 
              onClick={async () => {
                if (!newGroupName.trim()) return;
                try {
                  const res = await fetch('/api/users/groups', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ name: newGroupName, description: newGroupDesc })
                  });
                  if (res.ok) {
                    const newGroup = await res.json();
                    setGroups([...groups, newGroup]);
                    setNewGroupName('');
                    setNewGroupDesc('');
                    setShowNewGroup(false);
                  }
                } catch (err) { console.error(err); }
              }}
              style={{ padding: '8px 16px', background: '#22c55e', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
            >
              Create Group
            </button>
          </div>
        )}
        
        {/* Group List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {groups.length === 0 ? (
            <p style={{ color: subTextColor }}>No groups yet. Create one to get started.</p>
          ) : groups.map(group => (
            <div
              key={group.id}
              onClick={() => setSelectedGroupId(group.id)}
              style={{
                padding: '12px 16px',
                borderRadius: 4,
                border: `2px solid ${selectedGroupId === group.id ? accentColor : borderColor}`,
                background: selectedGroupId === group.id ? (darkMode ? '#374151' : '#eff6ff') : 'transparent',
                cursor: 'pointer'
              }}
            >
              <div style={{ color: textColor, fontWeight: 'bold' }}>{group.name}</div>
              {group.description && <div style={{ color: subTextColor, fontSize: 13 }}>{group.description}</div>}
            </div>
          ))}
        </div>
      </div>
      
      {/* Group Permissions (when selected) */}
      {selectedGroupId && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ color: textColor, marginTop: 0 }}>
              Edit: {groups.find(g => g.id === selectedGroupId)?.name}
            </h3>
            <button 
              onClick={() => setSelectedGroupId(null)}
              style={{ padding: '8px 12px', background: '#6b7280', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
            >
              Done
            </button>
          </div>
          
          {/* Widget Permissions */}
          <div>
            <h4 style={{ color: textColor, marginBottom: 12 }}>Widget Access</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {businessWidgets.concat(infraWidgets).map(w => (
                <button
                  key={w}
                  onClick={() => toggleWidget(w)}
                  style={{
                    padding: isMobile ? '4px 8px' : '6px 12px',
                    borderRadius: 4,
                    border: `1px solid ${selectedUserWidgets.includes(w) ? '#22c55e' : borderColor}`,
                    background: selectedUserWidgets.includes(w) ? '#22c55e' : 'transparent',
                    color: selectedUserWidgets.includes(w) ? 'white' : textColor,
                    cursor: 'pointer',
                    fontSize: isMobile ? 11 : 13,
                    minWidth: isMobile ? '60px' : 'auto',
                    flex: isMobile ? '0 0 auto' : 'auto'
                  }}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>
          
          {/* Tab Permissions */}
          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ color: textColor, margin: 0 }}>Tab Access</h4>
              <button 
                onClick={handleResetTabs}
                style={{ padding: '4px 8px', background: '#6b7280', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
              >
                Allow All
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {ALL_TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => toggleTab(tab.id)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 4,
                    border: `1px solid ${(selectedUserTabs === null || selectedUserTabs.includes(tab.id)) ? '#22c55e' : borderColor}`,
                    background: (selectedUserTabs === null || selectedUserTabs.includes(tab.id)) ? '#22c55e' : 'transparent',
                    color: (selectedUserTabs === null || selectedUserTabs.includes(tab.id)) ? 'white' : textColor,
                    cursor: 'pointer',
                    fontSize: 13
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          
          <button 
            onClick={handleSaveUserSettings}
            style={{ 
              marginTop: 20, 
              padding: '10px 20px', 
              background: accentColor, 
              color: 'white', 
              border: 'none', 
              borderRadius: 4, 
              cursor: 'pointer',
              fontSize: 14 
            }}
          >
            {userSaved ? '✓ Saved!' : 'Save Group Settings'}
          </button>
        </div>
      )}
    </>
  );

  // ==================== MAIN RENDER ====================
  return (
    <div style={{ padding: isMobile ? 12 : 20, overflowX: 'hidden', maxWidth: '100%' }}>
      <h1 style={{ color: textColor, marginBottom: 20 }}>Settings</h1>
      
      {/* Tab Bar */}
      {canManage && (
        <div style={{ marginBottom: 20, borderBottom: `1px solid ${borderColor}`, overflowX: 'auto', whiteSpace: 'nowrap' }}>
          <button style={getTabStyle('profile')} onClick={() => setActiveTab('profile')}>
            My Profile
          </button>
          <button style={getTabStyle('personal')} onClick={() => setActiveTab('personal')}>
            My Widgets
          </button>
          <button style={getTabStyle('users')} onClick={() => setActiveTab('users')}>
            Users
          </button>
          <button style={getTabStyle('groups')} onClick={() => setActiveTab('groups')}>
            Groups
          </button>
        </div>
      )}
      
      {/* Tab Content */}
      {!canManage ? (
        activeTab === 'profile' ? renderProfileTab() : renderPersonalTab()
      ) : (
        activeTab === 'profile' ? renderProfileTab() :
        activeTab === 'personal' ? renderPersonalTab() :
        activeTab === 'users' ? renderUsersTab() :
        renderGroupsTab()
      )}
    </div>
  );
};

export default Settings;
